/**
 * React Query hook for polling integration data-deletion job status.
 *
 * Hits `/api/orgs/<orgId>/integrations/<integrationEntityId>/delete-synced-data/`
 * and returns structured deletion-job status data.  Polls every 2 seconds while
 * any job is active and stops automatically once all jobs reach a terminal state.
 */

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { fetchWithDefaults } from '@/lib/http/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger({ module: 'useDeletionStatus' });

// Status values match backend DeletionJob.Status enum:
// 'active'    = DeletionJob.Status.ACTIVE
// 'completed' = DeletionJob.Status.COMPLETED
// 'failed'    = DeletionJob.Status.FAILED
// 'cancelled' = DeletionJob.Status.CANCELLED
export type DeletionJobStatus = 'active' | 'completed' | 'failed' | 'cancelled';

export interface DeletionJobProgress {
  items_processed: number;
  total_estimated: number | null;
  percent_complete: number;
  estimated_completion_at: string | null;
}

export interface DeletionJob {
  id: number;
  entity_type: string;
  status: DeletionJobStatus;
  items_processed: number;
  items_estimated: number | null;
  items_failed: number;
  progress: DeletionJobProgress;
  started_at: string | null;
  completed_at: string | null;
  last_progress_at: string | null;
  last_error: string;
}

// Zod schemas for runtime validation — mirror ImportJobProgressSchema / ImportJobSchema
// pattern from useImportStatus.ts:54-73
const DeletionJobProgressSchema = z.object({
  items_processed: z.number().int().nonnegative(),
  total_estimated: z.number().int().nonnegative().nullable(),
  percent_complete: z.number().min(0).max(100),
  estimated_completion_at: z.string().nullable(),
});

const DeletionJobSchema = z.object({
  id: z.number(),
  entity_type: z.string(),
  status: z.enum(['active', 'completed', 'failed', 'cancelled']),
  items_processed: z.number().int().nonnegative(),
  items_estimated: z.number().int().nonnegative().nullable(),
  items_failed: z.number().int().nonnegative(),
  progress: DeletionJobProgressSchema,
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  last_progress_at: z.string().nullable(),
  last_error: z.string(),
});

const DeletionStatusResponseSchema = z.object({
  jobs: z.array(DeletionJobSchema),
});

/**
 * Mirrors the backend deletion-email threshold (`LONG_IMPORT_THRESHOLD` at
 * `apps/vendor_imports/notifications.py:27`). When the deletion ETA exceeds
 * this duration, the UI promises to email the user instead of showing the
 * countdown — and the backend honours that promise by sending the email
 * at the COMPLETED transition.
 */
export const LONG_DELETION_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
export const LONG_DELETION_MESSAGE = "We'll email you when it's done";

interface FetchResult {
  jobs: DeletionJob[];
  error: string | null;
}

/** Fetch deletion job status for a specific integration. */
async function fetchDeletionStatus(
  orgId: string,
  integrationEntityId: string
): Promise<FetchResult> {
  const url = `/api/orgs/${orgId}/integrations/${integrationEntityId}/delete-synced-data/`;

  // 5-second timeout protection to prevent indefinite hangs during server outages
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  let response: Response;
  try {
    response = await fetchWithDefaults(url, { signal: controller.signal });
  } catch (error) {
    const isTimeout = (error as Error)?.name === 'AbortError';
    logger.error('Failed to fetch deletion status', error as Error, {
      orgId,
      integrationEntityId,
      endpoint: url,
      error_type: isTimeout ? 'timeout' : 'network_error',
      timeout_ms: 5000,
    });
    return { jobs: [], error: 'Unable to load deletion status' };
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    logger.error('Deletion status request returned non-2xx', undefined, {
      status: response.status,
      orgId,
      integrationEntityId,
      endpoint: url,
    });
    return { jobs: [], error: 'Unable to load deletion status' };
  }

  try {
    const raw = await response.json();
    const validation = DeletionStatusResponseSchema.safeParse(raw);
    if (!validation.success) {
      logger.error(
        'Deletion status response failed schema validation',
        undefined,
        {
          orgId,
          integrationEntityId,
          endpoint: url,
          validation_errors: validation.error.errors,
        }
      );
      return { jobs: [], error: 'Unable to load deletion status' };
    }
    return { jobs: validation.data.jobs, error: null };
  } catch (error) {
    logger.error('Failed to parse deletion status response', error as Error, {
      orgId,
      integrationEntityId,
      endpoint: url,
    });
    return { jobs: [], error: 'Unable to load deletion status' };
  }
}

interface UseDeletionStatusOptions {
  orgId: string;
  integrationEntityId: string;
}

export interface DeletionStatus {
  jobs: DeletionJob[];
  hasActiveDeletions: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Returns the refetch interval (ms) based on the current cached query result.
 * Exported for unit testing.
 */
export function computeRefetchInterval(
  data: FetchResult | undefined
): number | false {
  if (!data) {
    return 2000;
  }
  // Continue polling on transient errors so the user recovers automatically.
  if (data.error) {
    return 2000;
  }
  // Defensive: if some other code path seeded the cache with a malformed
  // entry (missing `jobs`), don't crash — treat as "no active jobs".
  const { jobs } = data;
  if (!Array.isArray(jobs)) {
    return 2000;
  }
  const hasActiveJobs = jobs.some(j => j.status === 'active');
  return hasActiveJobs ? 2000 : false;
}

/**
 * Hook for polling data-deletion job status for a specific integration.
 *
 * Polls every 2 seconds while any job is active; stops polling once all jobs
 * reach a terminal state (completed, failed, or cancelled).
 */
export function useDeletionStatus({
  orgId,
  integrationEntityId,
}: UseDeletionStatusOptions): DeletionStatus {
  const enabled = Boolean(orgId) && Boolean(integrationEntityId);

  const { data, isLoading, error } = useQuery({
    queryKey: ['deletion-status', orgId, integrationEntityId],
    queryFn: () => fetchDeletionStatus(orgId, integrationEntityId),
    enabled,
    staleTime: 2000,
    refetchInterval: query => computeRefetchInterval(query.state.data),
  });

  if (!enabled) {
    return {
      jobs: [],
      hasActiveDeletions: false,
      isLoading: false,
      error: null,
    };
  }

  if (isLoading && !data) {
    return {
      jobs: [],
      hasActiveDeletions: false,
      isLoading: true,
      error: null,
    };
  }

  if (error && !data) {
    return {
      jobs: [],
      hasActiveDeletions: false,
      isLoading: false,
      error: 'Unable to load deletion status',
    };
  }

  const jobs: DeletionJob[] = data?.jobs ?? [];
  const hasActiveDeletions = jobs.some(j => j.status === 'active');

  return {
    jobs,
    hasActiveDeletions,
    isLoading: false,
    error: data?.error ?? null,
  };
}
